import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  CheckCircle, 
  Loader2, 
  Users, 
  UserPlus,
  Calendar,
  ArrowRight,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodPantryCheckinKioskPage = () => {
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location') || 'fp-nashville';
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  
  // Form state
  const [step, setStep] = useState('type'); // 'type', 'form', 'confirm', 'success'
  const [clientType, setClientType] = useState(null); // 'new' or 'returning'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    dobMonth: '',
    dobDay: '',
    dobYear: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // Fetch kiosk configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/checkin/config/${locationId}`);
        setConfig(response.data);
        setConfigError(null);
      } catch (error) {
        console.error('Failed to load kiosk config:', error);
        setConfigError('Unable to load kiosk configuration. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [locationId]);

  // Auto-reset countdown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && step === 'success') {
      resetForm();
    }
  }, [countdown, step]);

  const resetForm = useCallback(() => {
    setStep('type');
    setClientType(null);
    setFormData({ firstName: '', lastName: '', dateOfBirth: '', dobMonth: '', dobDay: '', dobYear: '' });
    setSubmitError(null);
    setSuccessData(null);
    setCountdown(0);
  }, []);

  const handleTypeSelect = (type) => {
    setClientType(type);
    setStep('form');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) return 'Please enter your first name';
    if (!formData.lastName.trim()) return 'Please enter your last name';
    if (clientType === 'returning') {
      if (!formData.dobMonth || !formData.dobDay || !formData.dobYear) {
        return 'Please enter your complete date of birth for verification';
      }
      // Validate date parts
      const month = parseInt(formData.dobMonth, 10);
      const day = parseInt(formData.dobDay, 10);
      const year = parseInt(formData.dobYear, 10);
      if (month < 1 || month > 12) return 'Please enter a valid month (1-12)';
      if (day < 1 || day > 31) return 'Please enter a valid day (1-31)';
      if (year < 1900 || year > new Date().getFullYear()) return 'Please enter a valid year';
    }
    return null;
  };

  const handleCheckIn = async () => {
    const error = validateForm();
    if (error) {
      setSubmitError(error);
      return;
    }
    setSubmitError(null);
    
    // Submit directly without confirmation step
    setSubmitting(true);

    try {
      // Format date of birth from segmented fields (YYYY-MM-DD)
      let dateOfBirth = null;
      if (clientType === 'returning' && formData.dobYear && formData.dobMonth && formData.dobDay) {
        const year = formData.dobYear.padStart(4, '0');
        const month = formData.dobMonth.padStart(2, '0');
        const day = formData.dobDay.padStart(2, '0');
        dateOfBirth = `${year}-${month}-${day}`;
      }

      const payload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        client_type: clientType,
        date_of_birth: dateOfBirth,
        location_id: locationId
      };

      const response = await axios.post(`${API}/checkin/submit`, payload);
      setSuccessData(response.data);
      setStep('success');
      setCountdown(response.data.reset_seconds || config?.form_reset_seconds || 10);
    } catch (error) {
      console.error('Check-in failed:', error);
      setSubmitError(
        error.response?.data?.detail || 
        'Check-in submission failed. Please try again or use the "I\'m New Here" option.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 'form') {
      setStep('type');
      setClientType(null);
    } else if (step === 'confirm') {
      setStep('form');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(302,58%,25%)] via-[hsl(302,58%,30%)] to-[hsl(302,58%,25%)] flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4" />
          <p className="text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(302,58%,25%)] via-[hsl(302,58%,30%)] to-[hsl(302,58%,25%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="pt-8 pb-8">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Configuration Error</h2>
            <p className="text-muted-foreground mb-6">{configError}</p>
            <Button onClick={() => window.location.reload()} size="lg">
              <RefreshCw className="mr-2 h-5 w-5" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(302,58%,25%)] via-[hsl(302,58%,30%)] to-[hsl(302,58%,25%)] flex flex-col" data-testid="kiosk-checkin-page">
      {/* Header */}
      <header className="p-6 text-center text-white">
        <div className="flex items-center justify-center gap-3 mb-2">
          <img 
            src="/myhelp-logo.png" 
            alt="MyHELP" 
            className="h-12 w-auto brightness-0 invert"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold">{config?.location_name || 'Food Pantry'}</h1>
        <p className="text-purple-200 text-lg md:text-xl mt-1">Client Check-In</p>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        {/* Step: Select Client Type */}
        {step === 'type' && (
          <div className="w-full max-w-4xl" data-testid="kiosk-step-type">
            <Card className="border-0 shadow-2xl">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl md:text-3xl">Welcome!</CardTitle>
                <CardDescription className="text-lg md:text-xl">
                  Please select one of the options below
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-10">
                <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                  {/* Returning Client */}
                  <button
                    onClick={() => handleTypeSelect('returning')}
                    className="group p-8 md:p-12 rounded-2xl border-2 border-purple-200 hover:border-primary hover:bg-purple-50 transition-all duration-200 text-center focus:outline-none focus:ring-4 focus:ring-purple-300"
                    data-testid="btn-returning-client"
                  >
                    <Users className="h-16 w-16 md:h-20 md:w-20 mx-auto mb-4 text-primary group-hover:scale-110 transition-transform" />
                    <h3 className="text-2xl md:text-3xl font-bold text-primary mb-2">
                      I&apos;ve Been Here Before
                    </h3>
                    <p className="text-muted-foreground text-lg">
                      Returning client check-in
                    </p>
                  </button>

                  {/* New Client */}
                  <button
                    onClick={() => handleTypeSelect('new')}
                    className="group p-8 md:p-12 rounded-2xl border-2 border-fuchsia-200 hover:border-fuchsia-500 hover:bg-fuchsia-50 transition-all duration-200 text-center focus:outline-none focus:ring-4 focus:ring-fuchsia-300"
                    data-testid="btn-new-client"
                  >
                    <UserPlus className="h-16 w-16 md:h-20 md:w-20 mx-auto mb-4 text-fuchsia-600 group-hover:scale-110 transition-transform" />
                    <h3 className="text-2xl md:text-3xl font-bold text-fuchsia-700 mb-2">
                      I&apos;m New Here
                    </h3>
                    <p className="text-muted-foreground text-lg">
                      First-time visitor
                    </p>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step: Enter Information */}
        {step === 'form' && (
          <div className="w-full max-w-2xl" data-testid="kiosk-step-form">
            <Card className="border-0 shadow-2xl">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl md:text-3xl">
                  {clientType === 'returning' ? 'Welcome Back!' : 'Welcome!'}
                </CardTitle>
                <CardDescription className="text-lg">
                  Please enter your information below
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-10 space-y-6">
                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center text-lg">
                    {submitError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-lg font-medium">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    placeholder="Enter your first name"
                    className="h-14 text-xl px-4"
                    autoComplete="off"
                    data-testid="input-first-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-lg font-medium">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    placeholder="Enter your last name"
                    className="h-14 text-xl px-4"
                    autoComplete="off"
                    data-testid="input-last-name"
                  />
                </div>

                {clientType === 'returning' && (
                  <div className="space-y-2">
                    <Label className="text-lg font-medium flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Date of Birth
                    </Label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          value={formData.dobMonth}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                            handleFormChange('dobMonth', val);
                            if (val.length === 2) {
                              document.getElementById('dob-day')?.focus();
                            }
                          }}
                          placeholder="MM"
                          className="h-14 text-xl px-4 text-center"
                          autoComplete="off"
                          data-testid="input-dob-month"
                        />
                        <span className="text-xs text-muted-foreground text-center block mt-1">Month</span>
                      </div>
                      <div className="flex-1">
                        <Input
                          id="dob-day"
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          value={formData.dobDay}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                            handleFormChange('dobDay', val);
                            if (val.length === 2) {
                              document.getElementById('dob-year')?.focus();
                            }
                          }}
                          placeholder="DD"
                          className="h-14 text-xl px-4 text-center"
                          autoComplete="off"
                          data-testid="input-dob-day"
                        />
                        <span className="text-xs text-muted-foreground text-center block mt-1">Day</span>
                      </div>
                      <div className="flex-[1.5]">
                        <Input
                          id="dob-year"
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          value={formData.dobYear}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                            handleFormChange('dobYear', val);
                          }}
                          placeholder="YYYY"
                          className="h-14 text-xl px-4 text-center"
                          autoComplete="off"
                          data-testid="input-dob-year"
                        />
                        <span className="text-xs text-muted-foreground text-center block mt-1">Year</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="flex-1 h-14 text-lg"
                    disabled={submitting}
                    data-testid="btn-back"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCheckIn}
                    className="flex-1 h-14 text-lg bg-green-600 hover:bg-green-700"
                    disabled={submitting}
                    data-testid="btn-checkin"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Checking In...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-5 w-5" />
                        Check In
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="w-full max-w-2xl" data-testid="kiosk-step-success">
            <Card className="border-0 shadow-2xl border-purple-200 bg-purple-50">
              <CardContent className="p-8 md:p-12 text-center">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-14 w-14 md:h-20 md:w-20 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                  Check-In Complete!
                </h2>
                
                {/* Display queue number prominently if available */}
                {successData?.queue_number && (
                  <div className="mb-6">
                    <p className="text-lg text-purple-600 mb-2">Your Number</p>
                    <div className="inline-block bg-white border-4 border-primary rounded-2xl px-8 py-4">
                      <span className="text-5xl md:text-6xl font-bold text-primary">
                        {successData.queue_number}
                      </span>
                    </div>
                  </div>
                )}
                
                <p className="text-xl md:text-2xl text-purple-600 mb-8">
                  {successData?.message || config?.confirmation_message || 
                   'Thank you. Please have a seat and wait for your number to be called.'}
                </p>
                
                {countdown > 0 && (
                  <div className="space-y-4">
                    <p className="text-muted-foreground text-lg">
                      This screen will reset in {countdown} seconds
                    </p>
                    <Button
                      variant="outline"
                      onClick={resetForm}
                      className="h-12 px-8 text-lg"
                      data-testid="btn-reset"
                    >
                      <RefreshCw className="mr-2 h-5 w-5" />
                      Start New Check-In
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-purple-200 text-sm">
        <p>Thank you for visiting {config?.location_name || 'our Food Pantry'}!</p>
      </footer>
    </div>
  );
};

export default FoodPantryCheckinKioskPage;
